import { Box, CircularProgress, Collapse, IconButton, Slider, Typography, useTheme } from '@mui/material'
import React, { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'

import { Icons, MEETINGS_VIRTUAL_BACKGROUNDS } from '../../utils/utilities'
import { useSocketsContext } from '../../contexts/sockets.context'
import MeetingAccessEditDialog from './MeetingAccessEditDialog'
import { BlurOn, Circle, Image } from '@mui/icons-material'
import { useMeetingStore } from '../../store/meeting.store'
import CustomSwitch from '../common/CustomSwitch'
import api from '../../service/api'
import VideoPreview from './VideoPreview'
import Chats from './RightDrawer/Chats'
import Participants from './RightDrawer/Participants'
import { getTransformedMeetingType } from '../../utils/meeting-utilities'
import { useOnlineSessionsSocketStore } from '../../store/onlinesessionsocket.store'

const solidColors = [
    { name: "Soft Blue", hex: "#4A90E2" },
    { name: "Warm Gray", hex: "#9B9B9B" },
    { name: "Teal Green", hex: "#3FBF9E" },
    { name: "Muted Purple", hex: "#8E44AD" },
    { name: "Sunset Orange", hex: "#E67E22" },
    { name: "Cool Mint", hex: "#7ED6C1" },
    { name: "Charcoal", hex: "#2C3E50" },
    { name: "Pastel Yellow", hex: "#F9E79F" }
]

const LeftSideCollapse = observer( () => {

    const MeetingStore = useMeetingStore()

    const [meetingSettings, setMeetingSettings] = useState( MeetingStore.getCurrentMeeting.meeting_settings )
    const [showEditAccessDialog, setShowEditAccessDialog] = useState( false )
    const [updatingSetting, setUpdatingSetting] = useState( false )


    const { palette, border } = useTheme()
    // const { socket, emitEvent } = useSocketsContext()
    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()
    const { meeting_id } = useParams()

    const collapseRef = useRef()


    const updateMeetingSetting = async ( settingKey, value ) => {
        try {
            setUpdatingSetting( settingKey )
            const newValues = { ...meetingSettings }
            newValues[settingKey] = value
            await api.onlineMeetings.updateMeetingSettings( meeting_id, { newValues } )
            setMeetingSettings( newValues )
            MeetingStore.setMeetingSettings( newValues )
            await OnlineSessionSocketStore.emit( 'meeting:updated-settings', { newValues } )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || `Failed to update the setting.` )
        } finally {
            setUpdatingSetting( false )
        }
    }

    // VIDEO SETTINGS

    const handleEffectChange = async ( effectType, value ) => {

        if ( !MeetingStore.getMeetingState.isVideoOn )
            return
        const newValues = { ...MeetingStore.getMeetingState.videoEffectValues, [effectType]: value }

        const processor = MeetingStore.getVideoProcessor

        if ( !processor ) return

        if ( effectType === "none" ) {
            await processor.disable()
        } else if ( effectType === "blur" ) {
            await processor.setOptions( { type: "blur", blurDegree: value } )
            await processor.enable()
        } else if ( effectType === "image" ) {
            await processor.setOptions( { type: "img", source: "/background.jpg" } )
            await processor.enable()
        } else if ( effectType === "color" ) {
            await processor.setOptions( { type: "color", color: solidColors[value].hex } )
            await processor.enable()
        } else if ( effectType === "img" ) {
            const imageElement = document.createElement( 'img' )
            imageElement.src = MEETINGS_VIRTUAL_BACKGROUNDS[value].src
            imageElement.onload = async () => {
                await processor.setOptions( { type: "img", source: imageElement } )
                await processor.enable()
            }
        }
        MeetingStore.setMeetingState( { videoEffect: effectType, videoEffectValues: newValues } )
    }

    const effect = MeetingStore.getMeetingState.videoEffect
    const effectValues = MeetingStore.getMeetingState.videoEffectValues
    const mediaAccessUpdateDisabled = !['discussion', 'class', 'webinar'].includes( MeetingStore.getCurrentMeeting.meeting_type )

    return (
        <Collapse ref={collapseRef} sx={{ transition: "all 0.2s ease", minHeight: 0, flex: MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen ? { md: "0 0 350px", sm: "0 0 250px", xs: "0 0 auto" } : "0 0 0", marginLeft: MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen ? "10px" : "0", position: { xs: "fixed", sm: "initial" }, top: "10px", right: "10px", bottom: "10px", zIndex: 1600 }} orientation='horizontal' in={MeetingStore.getMeetingRoomLayoutSettings?.isLeftSideCollapseOpen} >

            {showEditAccessDialog && <MeetingAccessEditDialog meeting={MeetingStore.getCurrentMeeting} open={showEditAccessDialog} setOpen={setShowEditAccessDialog} />}

            <Box border={border[1]} height="100%" borderRadius="20px" overflow="clip" bgcolor={palette.contentBg} maxWidth={{ md: "350px", sm: "250px", xs: "auto" }} width={{ md: "350px", sm: "250px", xs: "auto" }} display="flex" flexDirection="column" flexGrow={1}>
                <Box display="flex" alignItems="center" justifyContent="space-between" padding="10px 20px">
                    <Typography variant="h6" color="textPrimary" letterSpacing="1px" textTransform="uppercase" fontSize="14px">{MeetingStore.getMeetingRoomLayoutSettings.leftSideCollapseTab}</Typography>
                    <IconButton onClick={() => MeetingStore.setMeetingRoomLayoutSettings( { isLeftSideCollapseOpen: false } )} size="small">
                        {Icons.default.CloseIcon}
                    </IconButton>
                </Box>


                {MeetingStore.getMeetingRoomLayoutSettings.leftSideCollapseTab === 'participants' && <Participants setShowEditAccessDialog={setShowEditAccessDialog} />}

                {/* CHATS */}
                {MeetingStore.getMeetingRoomLayoutSettings.leftSideCollapseTab === 'chats' && <Chats />}


                {/* PARTICIPANTS SETTINGS */}
                {['meeting settings', 'webinar settings', 'class settings', 'hybrid-class settings'].includes( MeetingStore.getMeetingRoomLayoutSettings.leftSideCollapseTab ) && <Box overflow="auto" flexGrow={1} display="flex" flexDirection="column" >
                    <Box padding="10px 20px" sx={{ cursor: "pointer" }}>
                        <Box flexGrow={1} display="flex" alignItems="center" justifyContent="space-between" marginTop="5px">
                            <Typography fontSize="14px" fontWeight={600}>Send messages</Typography>
                            <Box display="flex" gap="10px" alignItems="center">
                                {updatingSetting === "enable_chats" && <CircularProgress size={14} />}
                                <CustomSwitch disabled={updatingSetting === "enable_chats" || MeetingStore.getCurrentMeeting.meeting_type === "hybrid-class"} color="primary" size='small' sx={{ transform: `scale(0.9)` }} checked={meetingSettings.enable_chats} onChange={e => updateMeetingSetting( 'enable_chats', e.target.checked )} />
                            </Box>
                        </Box>
                        <Typography width="calc(100% - 100px )" lineHeight="1rem" marginTop="0" fontSize="10px">
                            Participants can send chat messages to {MeetingStore.getCurrentMeeting.meeting_type === 'discussion' ? "meeting" : MeetingStore.getCurrentMeeting.meeting_type} chat and private messages to hosts.
                        </Typography>
                    </Box>
                    <Box padding="10px 20px" sx={{ cursor: "pointer" }}>
                        <Box flexGrow={1} display="flex" alignItems="center" justifyContent="space-between" marginTop="5px">
                            <Typography fontSize="14px" fontWeight={600}>Send reactions</Typography>
                            <Box display="flex" gap="10px" alignItems="center">
                                {updatingSetting === "enable_reactions" && <CircularProgress size={14} />}
                                <CustomSwitch disabled={updatingSetting === "enable_reactions" || MeetingStore.getCurrentMeeting.meeting_type === "hybrid-class"} color="primary" size='small' sx={{ transform: `scale(0.9)` }} checked={meetingSettings.enable_reactions} onChange={e => updateMeetingSetting( 'enable_reactions', e.target.checked )} />
                            </Box>
                        </Box>
                        <Typography width="calc(100% - 100px )" lineHeight="1rem" marginTop="0" fontSize="10px">
                            Participants can express their emotions through emojis.
                        </Typography>
                    </Box>
                    {mediaAccessUpdateDisabled && <Typography marginX="20px" padding="10px" borderRadius="10px" bgcolor={palette.warning.light + "33"} border={`2px solid ${palette.warning.dark}`} lineHeight="1rem" color="warning" marginTop="0" fontSize="10px">
                        Cannot update these in {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )} meetings, instead you can give these perimssions to individual user from participations list
                    </Typography>}
                    <Box padding="10px 20px" sx={{ cursor: "pointer" }}>
                        <Box flexGrow={1} display="flex" alignItems="center" justifyContent="space-between" marginTop="5px">
                            <Typography fontSize="14px" fontWeight={600}>Turn on microphone</Typography>
                            <Box display="flex" gap="10px" alignItems="center">
                                {updatingSetting === "enable_microphone" && <CircularProgress size={14} />}
                                <CustomSwitch disabled={mediaAccessUpdateDisabled || updatingSetting === "enable_microphone"} color="primary" size='small' sx={{ transform: `scale(0.9)` }} checked={meetingSettings.enable_microphone} onChange={e => updateMeetingSetting( 'enable_microphone', e.target.checked )} />
                            </Box>
                        </Box>
                        <Typography width="calc(100% - 100px )" lineHeight="1rem" marginTop="0" fontSize="10px">
                            Participants can turn on microphone to send their audio.
                        </Typography>
                    </Box>
                    <Box padding="10px 20px" sx={{ cursor: "pointer" }}>
                        <Box flexGrow={1} display="flex" alignItems="center" justifyContent="space-between" marginTop="5px">
                            <Typography fontSize="14px" fontWeight={600}>Turn on camera</Typography>
                            <Box display="flex" gap="10px" alignItems="center">
                                {updatingSetting === "enable_video_feed" && <CircularProgress size={14} />}
                                <CustomSwitch disabled={mediaAccessUpdateDisabled || updatingSetting === "enable_video_feed"} color="primary" size='small' sx={{ transform: `scale(0.9)` }} checked={meetingSettings.enable_video_feed} onChange={e => updateMeetingSetting( 'enable_video_feed', e.target.checked )} />
                            </Box>
                        </Box>
                        <Typography width="calc(100% - 100px )" lineHeight="1rem" marginTop="0" fontSize="10px">
                            Participants can turn on video to send their video feed.
                        </Typography>

                    </Box>
                    <Box padding="10px 20px" sx={{ cursor: "pointer" }}>
                        <Box flexGrow={1} display="flex" alignItems="center" justifyContent="space-between" marginTop="5px">
                            <Typography fontSize="14px" fontWeight={600}>Share screen</Typography>
                            <Box display="flex" gap="10px" alignItems="center">
                                {updatingSetting === "enable_screen_sharing" && <CircularProgress size={14} />}
                                <CustomSwitch disabled={mediaAccessUpdateDisabled || updatingSetting === "enable_screen_sharing"} color="primary" size='small' sx={{ transform: `scale(0.9)` }} checked={meetingSettings.enable_screen_sharing} onChange={e => updateMeetingSetting( 'enable_screen_sharing', e.target.checked )} />
                            </Box>
                        </Box>
                        <Typography width="calc(100% - 100px )" lineHeight="1rem" marginTop="0" fontSize="10px">
                            Participants can share their screen to the meeeting.
                        </Typography>
                    </Box>
                </Box>}

                {/* VIDEO SETTINGS */}
                {MeetingStore.getMeetingRoomLayoutSettings.leftSideCollapseTab === 'video Settings' && <Box overflow="auto" padding="10px" flexGrow={1} display="flex" flexDirection="column" >
                    {/* <Box bgcolor={palette.rowHover} borderRadius="20px" marginBottom="20px" width="100%" height="200px" > */}
                    {MeetingStore.getMeetingState.isVideoOn && <VideoPreview height='200px' styles={{ marginBottom: "20px" }} track={MeetingStore.getLocalVideoTrack} />}
                    {!MeetingStore.getMeetingState.isVideoOn && <Box maxHeight="180px" border={border[1]} borderRadius="20px" marginBottom="20px" bgcolor={palette.grey[900]} display="flex" flexDirection="column" alignItems="center" height="100%" justifyContent="center" >
                        <Typography textAlign="center" color={palette.common.white} variant='h6' component="p" >Camera is turned off!</Typography>
                        <Typography textAlign="center" color={palette.common.white} variant="subtitle2" fontSize="12px">Turn on the camera to see results</Typography>
                    </Box>}
                    {/* </Box> */}
                    <Box padding="20px 10px" width="100%">
                        <Typography variant='subtitle2' marginBottom="20px" display="block" color='GrayText' fontSize="14px" >Background Effects</Typography>
                        <Box display="grid" gridTemplateColumns={{ md: "repeat(4,1fr)", xs: "repeat(2,1fr)" }} flexWrap="wrap" gap="10px">
                            <Box color={palette.common.font} onClick={() => handleEffectChange( 'none' )} overflow="hidden" display="flex" justifyContent="center" alignItems="center" sx={{ cursor: "pointer", width: "70px", aspectRatio: "1 / 1", outline: `3px solid ${effect === 'none' ? palette.primary.main + "88" : palette.primary.main + "33"}`, bgcolor: palette.primary.main + "33" }} padding="10px" position="relative" borderRadius="20px" flexDirection="column" gap="5px">
                                {Icons.default.DoNotDisturb}
                                <Typography variant='subtitle2' fontSize="10px" textAlign="center" >None</Typography>
                            </Box>
                            <Box color={palette.common.font} onClick={() => handleEffectChange( 'blur', effectValues.blur || 0 )} overflow="hidden" display="flex" justifyContent="center" alignItems="center" sx={{ cursor: "pointer", width: "70px", aspectRatio: "1 / 1", outline: `3px solid ${effect === 'blur' ? palette.primary.main + "88" : palette.primary.main + "33"}`, bgcolor: palette.primary.main + "33" }} padding="10px" position="relative" borderRadius="20px" flexDirection="column" gap="5px">
                                <BlurOn />
                                <Typography variant='subtitle2' fontSize="10px" textAlign="center" >Blur</Typography>
                            </Box>
                            <Box color={palette.common.font} onClick={() => handleEffectChange( 'color', effectValues.color || 0 )} padding="10px" display="flex" justifyContent="center" alignItems="center" sx={{ cursor: "pointer", width: "70px", aspectRatio: "1 / 1", outline: `3px solid ${effect === 'color' ? palette.primary.main + "88" : palette.primary.main + "33"}`, bgcolor: palette.primary.main + "33" }} borderRadius="20px" flexDirection="column" gap="5px">
                                <Circle />
                                <Typography variant='subtitle2' fontSize="10px" textAlign="center" >Color</Typography>
                            </Box>
                            <Box color={palette.common.font} onClick={() => handleEffectChange( 'img', effectValues.img || 0 )} padding="10px" display="flex" justifyContent="center" alignItems="center" sx={{ cursor: "pointer", width: "70px", aspectRatio: "1 / 1", outline: `3px solid ${effect === 'img' ? palette.primary.main + "88" : palette.primary.main + "33"}`, bgcolor: palette.primary.main + "33" }} borderRadius="20px" flexDirection="column" gap="5px">
                                <Image />
                                <Typography variant='subtitle2' fontSize="10px" textAlign="center" >Image</Typography>
                            </Box>
                        </Box>
                        <Collapse in={effect !== 'none'} >
                            {effect === "blur" && <Box padding="10px">
                                <Typography variant='subtitle2'> Blur Index: {effectValues.blur}</Typography>
                                <Box maxWidth="120px">
                                    <Slider disabled={!MeetingStore.getMeetingState.isVideoOn} color='secondary' onChange={( e, value ) => handleEffectChange( 'blur', value )} min={1} step={1} max={3} />
                                </Box>
                            </Box>}
                            {effect === "color" && <Box display="flex" flexWrap="wrap" gap="10px" padding="20px">
                                {solidColors.map( ( color, index ) => (
                                    <Box key={color.hex} display="flex" width="70px" onClick={() => handleEffectChange( 'color', index )} padding="5px" bgcolor={effectValues.color === index ? palette.primary.light + "66" : "rgba(0,0,0,0)"} border={`1px solid ${effectValues.color === index ? palette.primary.dark : "rgba(0,0,0,0)"}`} sx={{ ":hover": { bgcolor: effectValues.color !== index ? palette.rowHover : "" }, cursor: "pointer" }} borderRadius="5px" flexDirection="column" alignItems="center" flexWrap="wrap" gap="10px">
                                        <Box sx={{ cursor: "pointer" }} key={color} bgcolor={color.hex} width="55px" height="35px" display="block" component="span" borderRadius="5px" border="1px solid white" />
                                        <Typography fontSize="10px" textAlign="center" component="span">{color.name}</Typography>
                                    </Box>
                                ) )}
                            </Box>}
                            {effect === "img" && <Box display="flex" flexWrap="wrap" gap="10px" padding="20px">
                                {MEETINGS_VIRTUAL_BACKGROUNDS.map( ( vbg, index ) => (
                                    <Box key={vbg.id} display="flex" width="70px" onClick={() => handleEffectChange( 'img', index )} padding="5px" bgcolor={effectValues.img === index ? palette.primary.light + "66" : "rgba(0,0,0,0)"} border={`1px solid ${effectValues.img === index ? palette.primary.dark : "rgba(0,0,0,0)"}`} sx={{ ":hover": { bgcolor: effectValues.img !== index ? palette.rowHover : "" }, cursor: "pointer" }} borderRadius="5px" flexDirection="column" alignItems="center" flexWrap="wrap" gap="10px">
                                        <Box sx={{ cursor: "pointer" }} width="55px" height="35px" display="block" component="img" src={vbg.src} borderRadius="5px" border="1px solid white" />
                                        <Typography fontSize="10px" textAlign="center" component="span">{vbg.label}</Typography>
                                    </Box>
                                ) )}
                            </Box>}
                        </Collapse>
                    </Box>
                </Box>}

            </Box>
        </Collapse >
    )
} )

export default LeftSideCollapse


/*



    const admitUser = ( user ) => {
        if ( socket && socket.current ) {
            socket.current?.emit( 'admit-user', { user_id: user.user_id, room_id: meeting_id }, ( { success, message } ) => {
                if ( success ) {
                    toast( `${user.name} can now join the meeting.` )
                    MeetingStore.addOrRemovePendingRequests( [user], "remove" )
                }
                if ( message )
                    toast( message )

            } )
        }
    }

    const denyUserEntry = ( user ) => {
        if ( socket && socket.current ) {
            socket.current?.emit( 'deny-user', { user_id: user.user_id, room_id: meeting_id }, ( { success, message } ) => {
                if ( success ) {
                    toast( `${user.name} wasn't allowed to join the meeting.` )
                    MeetingStore.addOrRemovePendingRequests( [user], "remove" )
                }
                if ( message )
                    toast( message )

            } )
        }
    }

    const admitAllJoinRequests = () => {
        if ( socket && socket.current ) {
            socket.current?.emit( 'admit-all-requested-users', { room_id: meeting_id }, ( { success, message } ) => {
                if ( success ) {
                    MeetingStore.setRequestPendingList( [] )
                }
                if ( message )
                    toast( message )
            } )
        }
    }

    const denyAllJoinRequests = () => {
        if ( socket && socket.current ) {
            socket.current?.emit( 'deny-all-requested-users', { room_id: meeting_id }, ( { success, message } ) => {
                if ( success ) {
                    MeetingStore.setRequestPendingList( [] )
                }
                if ( message )
                    toast( message )
            } )
        }
    }

{MeetingStore.getPendingRequests.length > 0 && <Box marginBottom="20px">
                            <Typography fontSize="12px" fontWeight={400} gutterBottom paddingX="10px" color='GrayText'>WAITING TO JOIN</Typography>
                            <Box overflow="clip" border={border[1]} borderRadius="10px">
                                <Box padding="5px 20px" onClick={() => setShowJoinRequests( prev => !prev )} alignItems="center" display="flex" justifyContent="space-between" borderBottom={showJoinRequests && border[1]} sx={{ cursor: "pointer", ":hover": { bgcolor: palette.rowHover } }}>
                                    <Typography variant='subtitle2'>Waiting to be admitted</Typography>
                                    <Box display="flex" gap="10px" alignItems="center">
                                        <Typography variant='subtitle2'>{MeetingStore.getPendingRequests.length}</Typography>
                                        <IconButton size='small'>{showJoinRequests ? Icons.small.KeyboardArrowUp : Icons.small.KeyboardArrowDown}</IconButton>
                                    </Box>
                                </Box>
                                <Collapse in={showJoinRequests} >
                                    <Box display="flex" padding="5px 10px" justifyContent="flex-end" gap="10px"  >
                                        <Button onClick={admitAllJoinRequests} sx={{ textTransform: "initial", fontSize: "14px" }} color='greyed'>
                                            Deny all
                                        </Button>
                                        <Button onClick={denyAllJoinRequests} sx={{ textTransform: "initial", fontSize: "14px" }} color='primary'>
                                            Admit all
                                        </Button>
                                    </Box>
                                    <Box maxHeight="200px" overflow="auto">
                                        {MeetingStore.getPendingRequests?.map( user => (
                                            <Box key={user.user_id} display="flex" justifyContent="space-between" alignItems="center" gap="10px" padding="10px">
                                                <Box flexGrow={1} display="flex" justifyContent="space-between" alignItems="center" gap="10px">
                                                    <Box display="flex" flexGrow={1} alignItems="center" gap="10px">
                                                        <Avatar sx={{ width: "30px", height: "30px" }} >{user.name?.[0]?.toUpperCase()}</Avatar>
                                                        <Tooltip disableInteractive placement='top' title={user?.name}>
                                                            <Typography fontSize="14px" fontWeight="500" noWrap >{user.name}</Typography>
                                                        </Tooltip>
                                                    </Box>
                                                    <Box >
                                                        <Tooltip disableInteractive title="Admit">
                                                            <IconButton onClick={() => admitUser( user )} color='success' size='small' >
                                                                {Icons.default.CheckIcon}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip disableInteractive title="Deny">
                                                            <IconButton onClick={() => denyUserEntry( user )} color='error' size='small' >
                                                                {Icons.default.PersonRemove}
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ) )}
                                    </Box>
                                </Collapse>
                            </Box>
                        </Box>}

*/